import { addYears, differenceInYears, eachYearOfInterval, isWithinInterval } from 'date-fns';
import { groupBy, last, orderBy } from 'lodash';
import {
  IsoDateStr,
  Maybe,
  Patient,
  UserMetricValues,
  UserMetrics,
  UserPersonalValues,
  ZonedDateTime,
} from '@csp/csp-common-model';
import { ZonedDateTimeFactory, ZonedDateTimeFormatter } from '@csp/csp-common-date-util';
import { DemographicValueType } from '../model/schedulingModels/DemographicValueType';
import { DemographicValue } from '../model/schedulingModels/DemographicValue';

export type DemographicValues = {
  demographicValues: DemographicValue[];
  getByTypeOldestFirst: (type: string) => DemographicValue[];
};

// Properties on UserMetrics that should be mapped to a DemographicValue
const validUserMetrics = [DemographicValueType.HEIGHT_CM, DemographicValueType.WEIGHT_KG] as const;
type ValidUserMetric = (typeof validUserMetrics)[number];

const userMetricToDemographicValue = (type: ValidUserMetric, userMetric: UserMetricValues): DemographicValue => {
  switch (type) {
    case DemographicValueType.HEIGHT_CM:
      return {
        type,
        value: userMetric.heightMetres ? String(userMetric.heightMetres * 100) : undefined,
        createdAt: userMetric.createdAt,
      };
    case DemographicValueType.WEIGHT_KG:
      return {
        type,
        value: userMetric.weightKg ? String(userMetric.weightKg) : undefined,
        createdAt: userMetric.createdAt,
      };
  }
};

/**
 * Transforms user metrics data into a list of demographic values, ordered from oldest to newest.
 * It includes only the values that have changed compared to their previous state.
 *
 * @param {UserMetrics} userMetrics - The current user metrics data object.
 * @returns {DemographicValue[]} An array of demographic values representing changes over time.
 */
const fromUserMetricsOldestFirst = (userMetrics: UserMetrics): DemographicValue[] => {
  const allUserMetrics = [...userMetrics.previousUserMetrics, userMetrics];

  // Map each valid key in userMetrics to a DemographicValue
  const demographicValues = allUserMetrics.flatMap(userMetric =>
    validUserMetrics.map(metricKey => userMetricToDemographicValue(metricKey, userMetric)),
  );

  // Group demographic values by type
  const groupedDemographicValues = groupBy(demographicValues, item => item.type);

  // Filter so that only DemographicValues where the value has changed remain
  const changedDemographicValues = Object.values(groupedDemographicValues).flatMap(values => {
    const sortedValues = orderBy(values, value => value.createdAt.unixTimeMillis, 'asc'); // Oldest DemographicValue first
    return sortedValues.filter((value, index) => {
      // Check if the value for the current DemographicValue has changed since the last iteration
      const previousValue = sortedValues[index - 1];
      return previousValue?.value !== value.value;
    });
  });

  return changedDemographicValues;
};

const validUserPersonal = [
  DemographicValueType.GENDER,
  DemographicValueType.AGE_DYNAMIC,
  DemographicValueType.AGE_STATIC,
] as const;

type ValidUserPersonal = (typeof validUserPersonal)[number];

const userPersonalToDemographicValue = (
  type: ValidUserPersonal,
  userPersonal: UserPersonalValues,
): DemographicValue => {
  switch (type) {
    case DemographicValueType.GENDER:
      return {
        type,
        value: userPersonal.gender,
        createdAt: userPersonal.createdAt,
      };

    case DemographicValueType.AGE_DYNAMIC:
    case DemographicValueType.AGE_STATIC:
      return {
        type,
        value: userPersonal.birthDate,
        createdAt: userPersonal.createdAt,
      };
  }
};

/**
 * Transforms user personal data into a list of demographic values, ordered from oldest to newest.
 * It includes only the values that have changed compared to their previous state.
 *
 * @param {Patient} patient  - The current patient data object.
 * @returns {DemographicValue[]} An array of demographic values representing changes over time.
 */
const fromUserPersonalOldestFirst = (patient: Patient): DemographicValue[] => {
  const allUserPersonal = [...patient.personal.previousPersonals, patient.personal];

  // Map each valid key in userPersonal to a DemographicValue
  const demographicValues = allUserPersonal.flatMap(personal =>
    validUserPersonal.map(personalKey => userPersonalToDemographicValue(personalKey, personal)),
  );

  // Group demographic values by type
  const groupedDemographicValues = groupBy(demographicValues, item => item.type);

  // Filter so that only DemographicValues where the value has changed remain
  const changedDemographicValues = Object.entries(groupedDemographicValues).flatMap(([type, values]) => {
    let sortedValues = orderBy(values, personal => personal.createdAt.unixTimeMillis, 'asc'); // Oldest userPersonal first

    if (type === DemographicValueType.AGE_DYNAMIC && sortedValues.length > 0) {
      sortedValues = getDynamicAgeChanges(sortedValues);
    } else if (type === DemographicValueType.AGE_STATIC) {
      sortedValues = getStaticAgeChanges(sortedValues, patient.studyStartDate);
    }

    return sortedValues.filter((value, index) => {
      // Check if the value for the current value has changed since the last iteration.
      const previousValue = sortedValues[index - 1];
      return previousValue?.value !== value.value;
    });
  });

  return changedDemographicValues;
};

const getStaticAgeChanges = (changes: DemographicValue[], studyStartDate: Maybe<ZonedDateTime>): DemographicValue[] => {
  if (!studyStartDate) {
    return [];
  } else {
    // Map changes with age as value compared to study start date.
    const ageChanges = changes.map(change => {
      if (!change.value || !studyStartDate) {
        return { ...change, value: undefined };
      } else {
        const age = differenceInYears(ZonedDateTimeFormatter.toIsoDateString(studyStartDate), change.value);
        return { ...change, value: String(age) };
      }
    });
    return ageChanges;
  }
};

const FUTURE_BIRTHDAY_YEARS = 20;
/** Split a list of birthDate changes into a list of more changes if there were birthdays occuring between changes, causing the age to change */
const getDynamicAgeChanges = (changes: DemographicValue[]): DemographicValue[] => {
  // Add a change 20 years into the future to make sure we get all upcoming birthdays included.
  const lastChange = last(changes);
  if (lastChange) {
    const futureLastCreatedAt = addYears(lastChange.createdAt.unixTimeMillis, FUTURE_BIRTHDAY_YEARS);
    changes.push({ ...lastChange, createdAt: ZonedDateTimeFactory.fromUnixTimeMillis(futureLastCreatedAt.valueOf()) });
  }

  const ageChanges = changes.reduce<DemographicValue[]>((allChanges, currentChange, index) => {
    if (!currentChange.value) {
      return allChanges;
    }
    const nextChange = changes[index + 1];
    if (nextChange?.value) {
      const birthdaysWithinInterval = getBirthdaysBetweenInterval(
        currentChange.createdAt,
        nextChange.createdAt,
        currentChange.value,
      );

      // Re-map change value to age instead of date of birth. Used to compare with configured criterias.
      const firstChange = {
        ...currentChange,
        value: String(
          differenceInYears(ZonedDateTimeFormatter.toIsoDateString(currentChange.createdAt), currentChange.value),
        ),
      };

      const currentUserBirthday = currentChange.value;
      // Add all birthdays between the changes.
      const intervals: DemographicValue[] = birthdaysWithinInterval.map(birthday => {
        const birthdayIso = ZonedDateTimeFormatter.toIsoDateString(
          ZonedDateTimeFactory.fromUnixTimeMillis(birthday.valueOf()),
        );
        const age = differenceInYears(birthdayIso, currentUserBirthday);
        return {
          type: currentChange.type,
          value: String(age),
          createdAt: ZonedDateTimeFactory.fromUnixTimeMillis(birthday.valueOf()),
        };
      });

      return [...allChanges, firstChange, ...intervals];
    } else {
      const age = differenceInYears(
        ZonedDateTimeFormatter.toIsoDateString(currentChange.createdAt),
        currentChange.value,
      );
      return [...allChanges, { ...currentChange, value: String(age) }];
    }
  }, []);

  return ageChanges;
};

/** Calculate yearly occurences of a given date, between a given start and end date */
const getBirthdaysBetweenInterval = (startDate: ZonedDateTime, endDate: ZonedDateTime, date: IsoDateStr): Date[] => {
  const start = new Date(startDate.unixTimeMillis);
  const end = new Date(endDate.unixTimeMillis);
  const yearsBetween = eachYearOfInterval({
    start,
    end,
  });

  const currentBirthday = new Date(date);
  const userBirthdayMonth = currentBirthday.getMonth();
  const userBirthdayDay = currentBirthday.getDate();

  const birthdays = yearsBetween.map(date => {
    const newDate = new Date(date);
    newDate.setMonth(userBirthdayMonth, userBirthdayDay);
    return newDate;
  });

  const birthdaysWithinInterval = birthdays.filter(birthday => isWithinInterval(birthday, { start, end }));
  return birthdaysWithinInterval;
};

/**
 * Converts a patient's metrics and personal data into a structured demographic values object.
 * Combines all changes in metrics and personal data, ordered by timestamp, and provides a utility
 * to filter values by type.
 *
 * @param {Object} patient - The patient data.
 * @returns {DemographicValues} An object containing the changes in demographic values over time.
 */
const fromPatient = (patient: Patient): DemographicValues => {
  const metricDemographicValues = fromUserMetricsOldestFirst(patient.metrics);
  const personalDemographicValues = fromUserPersonalOldestFirst(patient);

  const demographicValuesOldestFirst = orderBy(
    [...metricDemographicValues, ...personalDemographicValues],
    demographicValue => demographicValue.createdAt.unixTimeMillis,
    'asc',
  );

  const getByTypeOldestFirst = (type: string): DemographicValue[] =>
    demographicValuesOldestFirst.filter(demographicValue => demographicValue.type === type);

  return {
    demographicValues: demographicValuesOldestFirst,
    getByTypeOldestFirst,
  };
};

export const DemographicValuesMapper = {
  fromPatient,
};
